import { getDocument } from '@atlaskit/browser-apis';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import type { AIPlugin } from '../../../aiPluginType';
import { isCollapseLoadingBarEnabledNoExposure } from '../../../ui/utils/collapse-loading-bar-enabled';
import type { StreamingViewState } from './streamingViewState';

/**
 * Manages editor state during streaming:
 * - Hides/shows cursor
 * - Disables/enables editor
 * - Manages focus and tab index
 * - Resets track changes baseline
 */
export function handleEditorState(
	view: EditorView,
	isActive: boolean,
	api: ExtractInjectionAPI<AIPlugin> | undefined,
	viewState: StreamingViewState,
	isFinalizing?: boolean,
): void {
	if (isFinalizing) {
		return;
	}
	const editorContentArea = view?.dom.closest<HTMLElement>('.ak-editor-content-area');
	if (viewState.originalTabIndex === undefined && editorContentArea) {
		viewState.originalTabIndex = editorContentArea.tabIndex;
		viewState.originalOutlineColor = editorContentArea.style.outlineColor;
		viewState.originalOutline = editorContentArea.style.outline;
	}

	// === STREAMING STARTED ===
	if (isActive && !viewState.hasActivatedStreaming) {
		viewState.hasActivatedStreaming = true;

		// Hide cursor and set up streaming state
		api?.core?.actions.execute(({ tr }: { tr: Transaction }) => {
			api?.selection?.commands.hideCursor?.(true)({ tr });
			api?.trackChanges?.commands.resetBaseline?.({ tr });
			api?.userIntent?.commands.setCurrentUserIntent?.('aiStreaming')({ tr });
			api?.editorDisabled?.commands.toggleDisabled?.(true)({ tr });
			return tr;
		});

		// Focus editor content area to enable keyboard shortcuts (e.g., Esc to cancel)
		if (editorContentArea) {
			editorContentArea.tabIndex = -1;
			editorContentArea.style.outline = 'none';
			const doc = getDocument();
			if (doc && !editorContentArea.contains(doc.activeElement)) {
				if (isCollapseLoadingBarEnabledNoExposure()) {
					editorContentArea.focus({ preventScroll: true });
				} else {
					editorContentArea.focus();
				}
			}
		}
	}

	// === STREAMING ENDED ===
	if (!isActive && viewState.hasActivatedStreaming) {
		viewState.hasActivatedStreaming = false;

		// Show cursor and restore editor state
		api?.core?.actions.execute(({ tr }: { tr: Transaction }) => {
			api?.selection?.commands.hideCursor?.(false)({ tr });
			api?.userIntent?.commands.setCurrentUserIntent?.('default')({ tr });
			api?.editorDisabled?.commands.toggleDisabled?.(false)({ tr });
			return tr;
		});

		// Restore original editor content area state
		if (editorContentArea) {
			restoreEditorContentArea(
				editorContentArea,
				viewState.originalTabIndex,
				viewState.originalOutlineColor,
				viewState.originalOutline,
			);
		}
	}
}

/**
 * Restores the editor content area to its original state.
 */
export function restoreEditorContentArea(
	editorContentArea: HTMLElement,
	originalTabIndex: number | undefined,
	originalOutlineColor: string | undefined,
	originalOutline: string | undefined,
): void {
	if (originalTabIndex !== undefined) {
		if (originalTabIndex === -1) {
			editorContentArea.removeAttribute('tabindex');
		} else {
			editorContentArea.tabIndex = originalTabIndex;
		}
	}
	if (originalOutlineColor !== undefined) {
		editorContentArea.style.outlineColor = originalOutlineColor;
		if (editorContentArea.getAttribute('style') === '') {
			editorContentArea.removeAttribute('style');
		}
	}
	if (originalOutline !== undefined) {
		editorContentArea.style.outline = originalOutline;
		if (editorContentArea.getAttribute('style') === '') {
			editorContentArea.removeAttribute('style');
		}
	}
}

/**
 * Restores the editor to its original state and clears view state.
 * Called during plugin cleanup/destroy.
 */
export function restoreEditorState(view: EditorView, viewState: StreamingViewState): void {
	const editorContentArea = view.dom.closest<HTMLElement>('.ak-editor-content-area');
	if (editorContentArea) {
		restoreEditorContentArea(
			editorContentArea,
			viewState.originalTabIndex,
			viewState.originalOutlineColor,
			viewState.originalOutline,
		);
	}
	viewState.originalTabIndex = undefined;
	viewState.originalOutlineColor = undefined;
	viewState.originalOutline = undefined;
}
